import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader, ArrowRight, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { useAuth } from '../App'; // Import Supabase Auth hook
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../supabaseClient';
import { forceRefreshSubscription, Subscription } from '../services/subscriptionService';

export const SubscriptionSuccess: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { session, loading: authLoading } = useAuth(); // Use Supabase session and loading state
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verificationAttempted, setVerificationAttempted] = useState(false);
  const [redirectTimer, setRedirectTimer] = useState<number | null>(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  const [subscriptionData, setSubscriptionData] = useState<Subscription | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [manualRefreshLoading, setManualRefreshLoading] = useState(false);

  // Get session_id from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get('session_id');
    if (sid) {
      setSessionId(sid);
    }
  }, [location]);

  // Force refresh the subscription data directly
  const forceRefresh = async () => {
    if (!session?.user?.id) return null;
    
    try {
      setManualRefreshLoading(true);
      console.log('Forcing direct subscription refresh...');
      const refreshedData = await forceRefreshSubscription(session.user.id);
      console.log('Force refresh result:', refreshedData);
      setSubscriptionData(refreshedData);
      setManualRefreshLoading(false);
      
      if (refreshedData) {
        // Start redirect timer
        setIsLoading(false);
        setRedirectTimer(5);
        return refreshedData;
      } else {
        console.log('Force refresh returned no data');
        return null;
      }
    } catch (err) {
      console.error('Error in force refresh:', err);
      setManualRefreshLoading(false);
      return null;
    }
  };

  // Check Stripe by calling our own API
  const checkStripeServerSide = async () => {
    if (!sessionId || !session?.user?.id) return null;
    
    try {
      console.log(`Checking Stripe via server for session ID: ${sessionId}`);
      
      // Call our verify endpoint with a special flag to force a Stripe check
      const response = await fetch(`/api/verify-session?session_id=${sessionId}&force_stripe_check=true`);
      
      if (!response.ok) {
        console.error('Error checking Stripe via server:', response.status);
        return null;
      }
      
      const data = await response.json();
      console.log('Stripe server check response:', data);
      
      if (data.success && data.stripe_checked) {
        console.log('Stripe checked successfully via server');
        // Try refresh one more time
        return await forceRefresh();
      }
      
      return null;
    } catch (err) {
      console.error('Error in server-side Stripe check:', err);
      return null;
    }
  };

  // Poll for subscription updates with exponential backoff
  useEffect(() => {
    // Only poll if we're still loading and verification was attempted
    if (!isLoading || !verificationAttempted || error || subscriptionData || !session?.user?.id) {
      return;
    }
    
    // Max polling attempts
    const MAX_POLL_ATTEMPTS = 20;
    if (pollAttempts >= MAX_POLL_ATTEMPTS) {
      console.log(`Reached max poll attempts (${MAX_POLL_ATTEMPTS})`);
      setError('Unable to confirm your subscription status automatically. Please check your dashboard shortly or contact support if the issue persists.');
      setIsLoading(false);
      return;
    }
    
    // Calculate exponential backoff
    const attempt = pollAttempts + 1;
    const waitTime = Math.min(Math.pow(1.5, attempt) * 1000, 10000); // Cap at 10 seconds
    
    console.log(`Polling attempt ${attempt}/${MAX_POLL_ATTEMPTS} - Waiting ${waitTime}ms`);
    
    // Setup polling with exponential backoff
    const pollTimer = setTimeout(async () => {
      console.log(`Executing poll attempt ${attempt} for subscription data...`);
      
      try {
        // Try direct check from database first
        const { data, error } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', session.user.id)
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
          
        if (error) {
          console.error('Error polling for subscription:', error);
        } else if (data) {
          console.log('Subscription found via polling:', data);
          setSubscriptionData(data as Subscription);
          setIsLoading(false);
          
          // Start redirect timer
          setRedirectTimer(5);
          return;
        } else {
          console.log('No subscription found yet on poll attempt', attempt);
        }
        
        // If not found in database, try force refresh
        if (attempt % 2 === 0) { // Every other attempt
          const forcedData = await forceRefresh();
          if (forcedData) {
            return;
          }
        }
        
        // If still not found and we're on attempt 5 or 10, try Stripe via server
        if ((attempt === 5 || attempt === 10) && sessionId) {
          const stripeData = await checkStripeServerSide();
          if (stripeData) {
            return;
          }
        }
        
        setPollAttempts(prev => prev + 1);
      } catch (err) {
        console.error('Error during polling:', err);
        setPollAttempts(prev => prev + 1);
      }
    }, waitTime);
    
    return () => clearTimeout(pollTimer);
  }, [isLoading, verificationAttempted, pollAttempts, error, session?.user?.id, subscriptionData, sessionId]);

  // Handle redirect timer
  useEffect(() => {
    if (redirectTimer === null) return;
    
    const timer = setInterval(() => {
      setRedirectTimer(prev => {
        if (!prev || prev <= 1) {
          clearInterval(timer);
          // Pass state to indicate we came from success page
          navigate('/app', { state: { from: { pathname: '/subscription/success' } } });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [redirectTimer, navigate]);
  
  // Set a timeout for the whole process
  useEffect(() => {
    // Set a global timeout of 60 seconds
    const timeout = setTimeout(() => {
      if (isLoading) {
        console.log('Global timeout reached, redirecting to app');
        setIsLoading(false);
        setError('The subscription process is taking longer than expected. Your subscription may still be processing. You can go to the dashboard to check your subscription status.');
      }
    }, 60000); // Extended to 60 seconds
    
    return () => clearTimeout(timeout);
  }, [isLoading]);

  useEffect(() => {
    // Wait for auth loading to finish before checking session
    if (authLoading) {
      return;
    }
    
    // If auth loaded and user is not signed in, redirect
    if (!session) {
      navigate('/auth'); // Redirect to Supabase auth page
      return;
    }

    async function verifySession() {
      // Only try verification once
      if (verificationAttempted) return;
      setVerificationAttempted(true);
      
      const localSessionId = sessionId;
      if (!localSessionId) {
        setError('Invalid session ID');
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      try {
        console.log(`[SubscriptionSuccess] Attempting to verify session: ${localSessionId}`);
        const verifyUrl = `/api/verify-session?session_id=${localSessionId}`;
        console.log(`[SubscriptionSuccess] Calling API: ${verifyUrl}`);
        
        // Make API call to verify the session
        const response = await fetch(verifyUrl);
        console.log(`[SubscriptionSuccess] API response status: ${response.status}`);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('[SubscriptionSuccess] Session verification failed with status:', response.status, errorText);
          setError(`Failed to verify your subscription (Status: ${response.status}). Please contact support.`);
          setIsLoading(false);
          return;
        }
        
        const data = await response.json();
        console.log("Session verification response:", data);
        
        if (!data.success) {
          setError(data.error || 'Failed to verify your subscription. Please contact support.');
          console.error('Session verification failed:', data);
          setIsLoading(false);
          return;
        }
        
        console.log(`Subscription verification successful. Plan type: ${data.plan_type}, Retry attempts: ${data.retry_attempts || 'N/A'}`);
        
        // Check if subscription was found during verification
        if (data.subscription_found && data.subscription_data) {
          console.log('Subscription data already available:', data.subscription_data);
          setSubscriptionData(data.subscription_data as Subscription);
          setIsLoading(false);
          setRedirectTimer(5);
        } else {
          console.log('Subscription not found yet, will start polling');
          // Polling will be handled by the polling effect
        }
      } catch (err) {
        console.error('Error verifying session:', err);
        setError('Could not verify your subscription. Please contact support.');
        setIsLoading(false);
      }
    }

    // Verify the session
    verifySession();
  }, [authLoading, session, sessionId, navigate, verificationAttempted]); 

  // Translation helper
  const getTranslation = (key: string, defaultText: string) => {
    try {
      // @ts-ignore - Ignore type checking for translation keys
      return t(key) || defaultText;
    } catch {
      return defaultText;
    }
  };

  // Show main loading state if auth is loading OR verification is loading
  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <Loader className="h-12 w-12 animate-spin text-indigo-600 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {getTranslation('subscription.processing', 'Processing Your Subscription')}
          </h1>
          <p className="text-gray-600 max-w-md mx-auto">
            {getTranslation('subscription.wait', 'Please wait while we confirm your subscription...')}
          </p>
          {pollAttempts > 0 && (
            <p className="text-sm text-gray-500 mt-4">
              Checking for subscription updates... ({pollAttempts}/20)
            </p>
          )}
          {pollAttempts > 10 && (
            <div className="mt-8">
              <p className="text-sm text-gray-500 mb-4">
                This is taking longer than expected. You can try manually refreshing:
              </p>
              <button
                onClick={forceRefresh}
                disabled={manualRefreshLoading}
                className="px-5 py-2 bg-indigo-600 rounded-md text-sm font-medium text-white hover:bg-indigo-700 flex items-center justify-center mx-auto"
              >
                {manualRefreshLoading ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                    Refreshing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh Subscription
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center max-w-md mx-auto">
          <XCircle className="h-12 w-12 text-red-600 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {error === 'Invalid session ID' 
              ? 'Invalid or Expired Session' 
              : getTranslation('subscription.error.title', 'Something Went Wrong')}
          </h1>
          <p className="text-gray-600 mb-8">
            {error === 'Invalid session ID'
              ? 'The checkout session ID is invalid or has expired. Please try again or contact support if the issue persists.'
              : error}
          </p>
          <div className="flex flex-col gap-4 items-center">
            <button
              onClick={forceRefresh}
              disabled={manualRefreshLoading}
              className="px-5 py-2 bg-indigo-600 rounded-md text-sm font-medium text-white hover:bg-indigo-700 w-full max-w-xs flex items-center justify-center"
            >
              {manualRefreshLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh Subscription
                </>
              )}
            </button>
            <div className="flex flex-col sm:flex-row gap-4 justify-center w-full max-w-xs">
              <button
                onClick={() => navigate('/app')}
                className="px-5 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 flex-1"
              >
                Go to Dashboard
              </button>
              <button
                onClick={() => navigate('/pricing')}
                className="px-5 py-2 border border-indigo-600 text-indigo-600 rounded-md text-sm font-medium hover:bg-indigo-50 flex-1"
              >
                View Plans
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="text-center max-w-md mx-auto">
        <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-6" />
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          {getTranslation('subscription.success.title', 'Subscription Confirmed!')}
        </h1>
        <p className="text-gray-600 mb-2">
          {getTranslation(
            'subscription.success.message',
            'Thank you for your subscription. You now have access to all premium features.'
          )}
        </p>
        <p className="text-sm text-gray-500 mb-8">
          {redirectTimer !== null 
            ? getTranslation(
                'subscription.redirecting',
                `Redirecting to your dashboard in ${redirectTimer} seconds...`
              )
            : getTranslation(
                'subscription.clickBelow',
                'Click below to go to your dashboard.'
              )
          }
        </p>
        <button
          onClick={() => navigate('/app')}
          className="px-5 py-2 bg-indigo-600 rounded-md text-sm font-medium text-white hover:bg-indigo-700"
        >
          {getTranslation('subscription.goToDashboard', 'Go to Dashboard')}
        </button>
      </div>
    </div>
  );
}; 